/**
 * Pagina pubblica di disiscrizione / riattivazione newsletter (redirect dal backend
 * dopo il click su un link email).
 *
 * mode="unsubscribe" (default): link di disiscrizione della newsletter.
 * mode="resubscribe": link "continua a ricevere" dell'email di cortesia inviata dopo il
 *                     blocco di un'entità; annulla il blocco.
 *
 * Il link dell'email NON agisce: questa pagina chiede conferma e solo allora
 * chiama il BE (POST /n/u/{token}[/entity] oppure POST /n/r/{token}).
 *
 * Legge la query string:
 *   - token  = <token destinatario>   (presente → si chiede conferma)
 *   - status = success | error        (esito già noto: token non valido, o conferma già eseguita)
 *   - scope  = global | entity        (tutte le newsletter o solo l'entità mittente)
 *   - entity = <nome entità>          (per personalizzare il messaggio)
 */
import { useState } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Typography, Box, Card, Button, Divider } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import PriorityHighRoundedIcon from '@mui/icons-material/PriorityHighRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import { DefaultContainedButton, DefaultContainedLoadingButton } from '../utils/Buttons';
import { unsubscribeApi } from '../services/newsletter/unsubscribeApi';

/** Icona e colori del badge per ciascuna fase. */
const BADGES = {
  confirm: { Icon: MailOutlineRoundedIcon, color: '#212b36', bg: '#F4F6F8' },
  cancelled: { Icon: CheckRoundedIcon, color: '#1B806A', bg: '#E9F7EF' },
  success: { Icon: CheckRoundedIcon, color: '#1B806A', bg: '#E9F7EF' },
  error: { Icon: PriorityHighRoundedIcon, color: '#B72136', bg: '#FDECEE' },
};

/** Testi per modalità e fase; `newsletters` è il destinatario dell'azione (con l'entità in evidenza). */
const TEXTS = {
  unsubscribe: {
    confirm: (newsletters) => ['Vuoi disiscriverti?', <>Confermando, non riceverai più {newsletters}.</>],
    cancelled: (newsletters) => ['Iscrizione mantenuta', <>Nessuna modifica: continuerai a ricevere {newsletters}.</>],
    success: (newsletters) => ['Disiscrizione completata', <>Non riceverai più {newsletters}.</>],
    error: () => ['Disiscrizione non riuscita', 'Il link potrebbe essere scaduto o non valido. Riprova più tardi.'],
    confirmLabel: 'Conferma disiscrizione',
    cancelLabel: "No, mantieni l'iscrizione",
  },
  resubscribe: {
    confirm: (newsletters) => ['Vuoi riattivare le newsletter?', <>Confermando, tornerai a ricevere {newsletters}.</>],
    cancelled: (newsletters) => ['Blocco mantenuto', <>Nessuna modifica: non riceverai {newsletters}.</>],
    success: (newsletters) => ['Newsletter riattivate', <>Tornerai a ricevere {newsletters}.</>],
    error: () => ['Riattivazione non riuscita', 'Il link potrebbe essere scaduto o non valido. Riprova più tardi.'],
    confirmLabel: 'Riattiva le newsletter',
    cancelLabel: 'No, lascia il blocco',
  },
};

/** Fase iniziale: con un esito in query string lo si mostra, con il token si chiede conferma. */
function initialPhase(searchParams) {
  const status = searchParams.get('status');
  if (status === 'error') return 'error';
  if (status === 'success') return 'success';
  return searchParams.get('token') ? 'confirm' : 'error';
}

export default function UnsubscribePage({ mode = 'unsubscribe' }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const isResubscribe = mode === 'resubscribe';
  const token = searchParams.get('token');
  const scope = searchParams.get('scope'); // 'global' | 'entity'
  const isEntity = scope === 'entity';

  const [phase, setPhase] = useState(() => initialPhase(searchParams)); // confirm | success | error | cancelled
  const [entity, setEntity] = useState(searchParams.get('entity')); // nome entità
  // Solo resubscribe: false se l'utente ha anche il blocco globale, che la riattivazione non tocca.
  const [allowNews, setAllowNews] = useState(true);
  const [loading, setLoading] = useState(false);

  const callConfirm = () => {
    if (isResubscribe) {
      return isEntity ? unsubscribeApi.confirmResubscribe(token) : unsubscribeApi.confirmResubscribeGlobal(token);
    }
    return isEntity ? unsubscribeApi.confirmEntity(token) : unsubscribeApi.confirmGlobal(token);
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const res = await callConfirm();
      const entityName = res.data?.entity ?? entity;
      setEntity(entityName);
      if (res.data?.allow_news === false) setAllowNews(false);
      setPhase('success');
      // Toglie il token dall'URL: ricaricando la pagina si rivede l'esito, non la richiesta di conferma.
      setSearchParams(
        {
          scope: scope || 'global',
          status: 'success',
          ...(isEntity && entityName ? { entity: entityName } : {}),
        },
        { replace: true }
      );
    } catch (e) {
      setPhase('error');
    } finally {
      setLoading(false);
    }
  };

  // Destinatario dell'azione, con il nome dell'entità in evidenza.
  let newsletters = 'tutte le nostre newsletter';
  if (isEntity) {
    newsletters = entity ? (
      <>
        le newsletter di{' '}
        <Box component="strong" sx={{ color: 'text.primary', fontWeight: 600 }}>
          {entity}
        </Box>
      </>
    ) : (
      'le newsletter di questo mittente'
    );
  }

  const texts = TEXTS[mode] ?? TEXTS.unsubscribe;
  const [title, message] = (texts[phase] ?? texts.error)(newsletters);
  const { Icon, color, bg } = BADGES[phase] ?? BADGES.error;

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>

      {/*
        minHeight (non height) + margin auto sulla card: se la card è più alta dello schermo
        la pagina si allunga e scorre. Con height fissa e centratura flex la parte alta della
        card usciva dal contenitore e non era raggiungibile con lo scroll (mobile).
      */}
      <Box
        className="default-bg"
        sx={{
          minHeight: '100%',
          width: '100%',
          display: 'flex',
          px: 2,
          py: { xs: 3, sm: 6 },
        }}
      >
        <Card
          sx={{
            m: 'auto',
            width: '100%',
            maxWidth: 440,
            px: { xs: 3, sm: 5 },
            py: { xs: 4, sm: 5 },
            borderRadius: 3,
            textAlign: 'center',
            boxShadow: '0 12px 40px rgba(33, 43, 54, 0.12)',
          }}
        >
          <Box
            component="img"
            src="/assets/images/img/logopd.png"
            alt="Partito Democratico"
            sx={{ width: 96, mx: 'auto', mb: 4, display: 'block' }}
          />

          <Box
            sx={{
              width: 56,
              height: 56,
              mx: 'auto',
              mb: 2.5,
              borderRadius: '50%',
              bgcolor: bg,
              color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon sx={{ fontSize: 28 }} />
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            {title}
          </Typography>

          {/* overflowWrap: un nome entità lungo senza spazi non deve allargare la card oltre lo schermo. */}
          <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6, overflowWrap: 'anywhere' }}>
            {message}
          </Typography>

          {isResubscribe && phase === 'success' && !allowNews && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2, lineHeight: 1.6 }}>
              Al momento hai disattivato la ricezione di tutte le newsletter: per riceverle di nuovo, riattivala dal tuo
              profilo.
            </Typography>
          )}

          <Box sx={{ mt: 4 }}>
            {phase === 'confirm' ? (
              <>
                <DefaultContainedLoadingButton fullWidth onClick={handleConfirm} loading={loading}>
                  {texts.confirmLabel}
                </DefaultContainedLoadingButton>
                <Button
                  fullWidth
                  variant="text"
                  onClick={() => setPhase('cancelled')}
                  disabled={loading}
                  sx={{ mt: 1, color: 'text.secondary', '&:hover': { bgcolor: '#F4F6F8' } }}
                >
                  {texts.cancelLabel}
                </Button>
              </>
            ) : (
              <DefaultContainedButton fullWidth to="/" component={RouterLink}>
                Vai all&apos;accesso
              </DefaultContainedButton>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="caption" component="p" sx={{ color: 'text.disabled', lineHeight: 1.5 }}>
            Puoi gestire le tue preferenze di comunicazione in qualsiasi momento dal tuo profilo.
          </Typography>
        </Card>
      </Box>
    </>
  );
}

UnsubscribePage.propTypes = {
  mode: PropTypes.oneOf(['unsubscribe', 'resubscribe']),
};
